import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatIst, inboxApi, type CampaignDetail, type CampaignRecipient } from '@wa/shared';
import { campaignNote } from '@/campaigns/CampaignStatusChip';
import { outcomeLabel } from '@/campaigns/CampaignBuilder';

type Outcome = 'all' | 'delivered' | 'read' | 'clicked' | 'failed' | 'skipped' | 'pending';

/**
 * What happened, once it has started happening.
 *
 * The numbers along the top are the summary; the list underneath exists for one
 * question — "which ones did not work, and why" — so it opens filtered rather than
 * making somebody scroll twenty thousand successes to find forty failures.
 */
export function CampaignReport({ campaign, onClose }: { campaign: CampaignDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<Outcome>('all');
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['campaign', campaign.id],
    queryFn: () => inboxApi.campaign(campaign.id),
    // Only while it is moving. A finished campaign is a static document, and
    // polling one forever is a request every few seconds that can never change.
    refetchInterval: campaign.status === 2 ? 3000 : false,
    initialData: { campaign, clicks: [] },
  });

  const current = detail.data?.campaign ?? campaign;
  const clicks = detail.data?.clicks ?? [];

  const recipients = useQuery({
    queryKey: ['campaign-recipients', campaign.id, outcome],
    queryFn: () => inboxApi.campaignRecipients(campaign.id, outcome === 'all' ? null : outcome),
  });

  const act = (fn: () => Promise<unknown>, fallback: string) => async () => {
    setError(null);
    try {
      await fn();
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback);
    }
  };

  const cancel = useMutation({
    mutationFn: () => inboxApi.cancelCampaign(campaign.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not cancel that.'),
  });

  const note = campaignNote(current);
  const percent = current.totalRecipients > 0
    ? Math.round(((current.sentCount + current.failedCount + current.skippedCount) / current.totalRecipients) * 100)
    : 0;

  return (
    <div className="pane-scroll h-full overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-5 p-3 sm:p-5">
        {error && (
          <p role="alert" className="rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}

        {note && (
          <p className="rounded-(--radius-panel) border border-(--color-warn) bg-(--color-warn)/8 px-3 py-2.5 text-small leading-relaxed text-(--color-ink-soft)">
            {note}
          </p>
        )}

        {current.retargetOfCampaignId && (
          <p className="text-small text-(--color-muted)">
            Sent to everyone from an earlier campaign whose message was {outcomeLabel(current.retargetOutcome)}.
          </p>
        )}

        <section>
          <div className="flex items-baseline justify-between">
            <h3 className="text-small font-semibold">Progress</h3>
            <span className="text-micro tabular-nums text-(--color-muted)">
              {(current.sentCount + current.failedCount + current.skippedCount).toLocaleString('en-IN')} of{' '}
              {current.totalRecipients.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-(--color-sunken)">
            <div
              className={`h-full rounded-full transition-[width] duration-500 ${
                current.status === 3 ? 'bg-(--color-warn)' : 'bg-(--color-brand)'
              }`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </section>

        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Sent" value={current.sentCount} />
          <Stat label="Delivered" value={current.deliveredCount} />
          <Stat label="Read" value={current.readCount} />
          <Stat label="Failed" value={current.failedCount} tone={current.failedCount > 0 ? 'bad' : undefined} />
          <Stat label="Skipped" value={current.skippedCount} />
        </dl>

        <p className="text-micro leading-relaxed text-(--color-muted)">
          Skipped means the message was never sent — almost always because the person
          opted out between the list being drawn up and their turn. Failed means
          WhatsApp refused it.
        </p>

        {clicks.length > 0 && (
          <section>
            <h3 className="mb-2 text-small font-semibold">Buttons tapped</h3>
            <ul className="flex flex-col divide-y divide-(--color-line-dim) rounded-(--radius-panel) border border-(--color-line)">
              {clicks.map((click) => (
                <li key={click.buttonPayload} className="flex items-center gap-3 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-small">
                    {click.buttonText ?? click.buttonPayload}
                  </span>
                  <span className="shrink-0 text-small font-medium tabular-nums">
                    {click.clicks.toLocaleString('en-IN')}
                  </span>
                  <span className="w-12 shrink-0 text-right text-micro text-(--color-muted)">
                    {current.deliveredCount > 0
                      ? `${Math.round((click.clicks / current.deliveredCount) * 100)}%`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-micro text-(--color-muted)">
              People, not taps — somebody tapping the same button twice is one.
            </p>
          </section>
        )}

        <section>
          {/* No negative margin. The usual -mx-1/px-1 bleed makes the row wider than
              its container, and with nothing clipping it the chips sit over the panel
              edge — which the layout check caught at every width above 768. */}
          <div className="chip-scroll mb-2 flex gap-1 overflow-x-auto">
            {(['all', 'failed', 'skipped', 'delivered', 'read', 'clicked', 'pending'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setOutcome(value)}
                className={[
                  'tap shrink-0 rounded-full px-2.5 py-1 text-micro font-medium capitalize transition',
                  outcome === value
                    ? 'bg-(--color-brand-wash) text-(--color-brand)'
                    : 'text-(--color-muted) hover:bg-(--color-sunken)',
                ].join(' ')}
              >
                {value === 'all' ? 'Everyone' : value}
              </button>
            ))}
          </div>

          {recipients.isPending ? (
            <p className="py-4 text-small text-(--color-muted)">Loading…</p>
          ) : (recipients.data?.items.length ?? 0) === 0 ? (
            <p className="rounded-(--radius-panel) border border-(--color-line) px-3 py-6 text-center text-small text-(--color-muted)">
              {outcome === 'failed' ? 'Nothing failed.' : 'Nobody here.'}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-(--color-line-dim) rounded-(--radius-panel) border border-(--color-line)">
              {recipients.data!.items.map((r) => <RecipientRow key={r.id} recipient={r} />)}
            </ul>
          )}
        </section>

        <div className="flex flex-wrap items-center gap-2 border-t border-(--color-line) pt-4">
          {current.status === 2 && (
            <button
              type="button"
              onClick={act(() => inboxApi.pauseCampaign(campaign.id), 'Could not pause that.')}
              className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small"
            >
              Pause
            </button>
          )}

          {current.status === 3 && (
            <button
              type="button"
              onClick={act(() => inboxApi.resumeCampaign(campaign.id), 'Could not resume that.')}
              className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-2 text-small font-medium text-white"
            >
              Resume
            </button>
          )}

          {[1, 2, 3].includes(current.status) && (
            <button
              type="button"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
              className="tap ml-auto rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small text-(--color-bad) disabled:opacity-40"
            >
              Cancel the rest
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small"
          >
            Close
          </button>
        </div>

        {[1, 2, 3].includes(current.status) && (
          <p className="text-micro leading-relaxed text-(--color-muted)">
            Cancelling stops what has not gone yet. Messages already accepted by
            WhatsApp cannot be recalled.
          </p>
        )}
      </div>
    </div>
  );
}

function RecipientRow({ recipient }: { recipient: CampaignRecipient }) {
  // The message's own status outranks the recipient row once there is one: the
  // dispatcher records "sent", and delivery and read arrive later as receipts.
  const label =
    recipient.status === 4 ? 'Skipped'
      : recipient.status === 3 ? 'Failed'
        : recipient.messageStatus === 3 ? 'Read'
          : recipient.messageStatus === 2 ? 'Delivered'
            : recipient.messageStatus === 4 ? 'Failed'
              : recipient.status === 2 ? 'Sent'
                : 'Waiting';

  const bad = label === 'Failed' || label === 'Skipped';
  const reason = recipient.failureReason ?? recipient.errorTitle;

  return (
    <li className="flex items-start gap-3 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-small">
          {recipient.displayName ?? `+${recipient.waId ?? ''}`}
        </p>
        {reason && (
          <p className={`mt-0.5 text-micro leading-snug ${bad ? 'text-(--color-bad)' : 'text-(--color-muted)'}`}>
            {reason}
          </p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <span className={`text-micro font-medium ${bad ? 'text-(--color-bad)' : 'text-(--color-muted)'}`}>
          {label}
        </span>
        {recipient.processedAt && (
          <p className="text-micro text-(--color-muted)">{formatIst(recipient.processedAt)}</p>
        )}
      </div>
    </li>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'bad' }) {
  return (
    <div className="rounded-(--radius-panel) border border-(--color-line) px-2 py-2 text-center">
      <dd className={`text-title font-semibold tabular-nums ${tone === 'bad' ? 'text-(--color-bad)' : ''}`}>
        {value.toLocaleString('en-IN')}
      </dd>
      <dt className="text-micro text-(--color-muted)">{label}</dt>
    </div>
  );
}
